// ============================================================================
// PRESETS — the four times and the four repeats the owner asked for (2026-09-14).
//
// "For reminders, it should be like: 1 hour from now · 11 am · 3 pm · 10 pm." And: "the ability
// to add recurring reminders (daily, weekly, monthly)". Yearly was proposed and he chose to skip
// it, because it would have needed a migration — the Worker's rule vocabulary (lib/recurrence.ts)
// and the tasks table's check constraint stop at MONTHLY, and this file offers exactly what the
// backend will store. A preset that the database would refuse is not a preset.
//
// ONE FILE, TWO SCREENS. The Reminders quick-add and the row's edit menu both read from here, so
// the chips say the same thing in both places, and a fifth preset one day is one line.
// ============================================================================

import type { ISODate } from '@app/shared'

export interface TimePreset {
  /** What the chip says. */
  label: string
  /** The 24-hour 'HH:MM' an `<input type="time">` takes. */
  hhmm: string
}

/** The three clock presets. "1 hour from now" is not here — it is a function of the clock. */
export const TIME_PRESETS: readonly TimePreset[] = [
  { label: '11 AM', hhmm: '11:00' },
  { label: '3 PM', hhmm: '15:00' },
  { label: '10 PM', hhmm: '22:00' },
]

/** The chip's label for the relative preset. One place, so both screens agree on the wording. */
export const IN_ONE_HOUR_LABEL = '1 hour from now'

/**
 * The LOCAL day and wall clock one hour from `now`.
 *
 * Local, not UTC: `toISODate` in @app/shared slices `toISOString()`, which is tomorrow's date from
 * 5pm Pacific onward — and "1 hour from now" at 11:30 PM really is tomorrow, while at 4:30 PM it
 * is not. The minute is rounded UP to the next multiple of five, because a reminder at 3:47 is a
 * clock nobody set on purpose, and rounding down would make "1 hour from now" arrive early.
 */
export function inOneHour(now: Date = new Date()): { date: ISODate; hhmm: string } {
  const at = new Date(now.getTime() + 60 * 60_000)
  const rounded = Math.ceil(at.getMinutes() / 5) * 5
  at.setMinutes(rounded, 0, 0)
  const pad = (n: number) => String(n).padStart(2, '0')
  return {
    date: `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}` as ISODate,
    hhmm: `${pad(at.getHours())}:${pad(at.getMinutes())}`,
  }
}

/** The repeats on offer. `rrule` is exactly what the Worker's check constraint accepts. */
export const REPEAT_OPTIONS: readonly { label: string; rrule: string | null }[] = [
  { label: 'None', rrule: null },
  { label: 'Daily', rrule: 'FREQ=DAILY' },
  { label: 'Weekly', rrule: 'FREQ=WEEKLY' },
  { label: 'Monthly', rrule: 'FREQ=MONTHLY' },
]

/**
 * The sentence under a repeat, kept honest about HOW it repeats.
 *
 * Recurrence here is materialise-on-complete (apps/worker/src/lib/recurrence.ts): the next
 * occurrence is created when this one is ticked off, and an unfinished one never re-fires. So the
 * copy says "after you complete it" rather than implying a standing alarm — a person who reads
 * "repeats daily" as "nags me daily" would be misled by the shorter sentence.
 */
export function repeatHint(rrule: string | null): string | null {
  const option = REPEAT_OPTIONS.find((o) => o.rrule === rrule)
  if (!option || option.rrule === null) return null
  return `repeats ${option.label.toLowerCase()} after you complete it`
}

/**
 * Is this rule one of the four the picker can show? A rule with an INTERVAL or a BYDAY — legal
 * on the Worker, written by hand or by an import — is not, and the picker must not silently
 * overwrite it with "None" the moment the menu opens. Callers add it as its own option instead.
 */
export function isPickableRepeat(rrule: string | null): boolean {
  return REPEAT_OPTIONS.some((o) => o.rrule === rrule)
}
